import React from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { PaginationType } from '~/util';

const itemsPerPage = [10, 25, 50, 100];

interface PaginationProps {
    pagination: PaginationType;
    updatePagination: (info: Partial<PaginationType>) => void;
    totalNumLimit?: number;
}
function Pagination({ pagination, updatePagination, totalNumLimit }: PaginationProps) {
    const currentNum = pagination.current || 0;
    const lastNum = pagination.last || 0;
    const totalNum = pagination.total || 0;
    const itemsPerPageNum = pagination.items_per_page || 0;
    let prefixEllipsisClass = 'd-block';
    if (currentNum === 3) prefixEllipsisClass = 'd-md-none';
    if (currentNum === 4) prefixEllipsisClass = 'd-lg-none';
    let suffixEllipsisClass = 'd-block';
    if (lastNum - currentNum === 2) suffixEllipsisClass = 'd-md-none';
    if (lastNum - currentNum === 3) suffixEllipsisClass = 'd-lg-none';

    function getLinkState(page: number) {
        const classes = [];
        if (page < 1 || page > lastNum) {
            classes.push('disabled');
        }
        return classes.join(' ');
    }

    function updatePag(page: null | number, perPage?: null | string) {
        let updatedItemsPerPage = pagination.items_per_page;
        if (perPage) updatedItemsPerPage = parseInt(perPage);
        const pag = {
            current: page || pagination.current,
            items_per_page: updatedItemsPerPage,
        };

        updatePagination(pag);
    }

    function getCurrentPageStart() {
        return (currentNum - 1) * itemsPerPageNum + 1;
    }

    function getCurrentPageEnd() {
        const lastVal = currentNum * itemsPerPageNum;
        return lastVal > totalNum ? pagination.total : lastVal;
    }

    function getTotalNum() {
        if (!totalNum) return '...';
        if (totalNumLimit && totalNum >= totalNumLimit) return `${totalNum}+`;
        return totalNum;
    }

    return (
        <div className="d-flex justify-content-between mt-2">
            <div className="d-inline-flex">
                <span className="py-1">Items per page:&nbsp;</span>
                <DropdownButton
                    id="page-size-select"
                    title={pagination.items_per_page}
                    onSelect={(eventKey) => updatePag(null, eventKey)}
                    menuRole="listbox"
                >
                    {itemsPerPage.map((num) => (
                        <Dropdown.Item eventKey={num} key={num} role="option">
                            {num}
                        </Dropdown.Item>
                    ))}
                </DropdownButton>
            </div>

            <ul className="pagination">
                <li className={`page-item ${getLinkState(currentNum - 1)}`}>
                    <button
                        className="page-link"
                        type="button"
                        onClick={() => updatePag(currentNum - 1)}
                    >
                        <span aria-hidden="true">&laquo;</span>
                        <span className="sr-only">Previous</span>
                    </button>
                </li>
                {currentNum > 1 && (
                    <li className="page-item">
                        <button className="page-link" type="button" onClick={() => updatePag(1)}>
                            1
                        </button>
                    </li>
                )}
                {currentNum > 2 && (
                    <li className={`${prefixEllipsisClass} page-item disabled`}>
                        <button className="page-link" type="button">
                            ...
                        </button>
                    </li>
                )}
                {currentNum - 2 > 1 && (
                    <li className="d-none d-lg-block page-item">
                        <button
                            className="page-link"
                            type="button"
                            onClick={() => updatePag(currentNum - 2)}
                        >
                            {currentNum - 2}
                        </button>
                    </li>
                )}
                {currentNum - 1 > 1 && (
                    <li className="d-none d-md-block page-item">
                        <button
                            className="page-link"
                            type="button"
                            onClick={() => updatePag(currentNum - 1)}
                        >
                            {currentNum - 1}
                        </button>
                    </li>
                )}
                <li className="page-item active">
                    <button className="page-link" type="button">
                        {currentNum}
                    </button>
                </li>
                {currentNum + 1 < lastNum && (
                    <li className="d-none d-md-block page-item">
                        <button
                            className="page-link"
                            type="button"
                            onClick={() => updatePag(currentNum + 1)}
                        >
                            {currentNum + 1}
                        </button>
                    </li>
                )}
                {currentNum + 2 < lastNum && (
                    <li className="d-none d-lg-block page-item">
                        <button
                            className="page-link"
                            type="button"
                            onClick={() => updatePag(currentNum + 2)}
                        >
                            {currentNum + 2}
                        </button>
                    </li>
                )}
                {currentNum + 1 < lastNum && (
                    <li className={`${suffixEllipsisClass} page-item disabled`}>
                        <button className="page-link" type="button">
                            ...
                        </button>
                    </li>
                )}
                {currentNum < lastNum && (
                    <li className="page-item">
                        <button
                            className="page-link"
                            type="button"
                            onClick={() => updatePag(lastNum)}
                        >
                            {lastNum}
                        </button>
                    </li>
                )}
                <li className={`page-item ${getLinkState(currentNum + 1)}`}>
                    <button
                        className="page-link"
                        type="button"
                        onClick={() => updatePag(currentNum + 1)}
                    >
                        <span aria-hidden="true">&raquo;</span>
                        <span className="sr-only">Next</span>
                    </button>
                </li>
            </ul>

            <div>
                {getCurrentPageStart()} - {getCurrentPageEnd()} of {getTotalNum()} items
            </div>
        </div>
    );
}

export { Pagination };
